import React from "react";

import { isBrowser } from "utils/browser";

interface TrackableItem {
    top: number;
    bottom: number;
    progress: number;
}

interface TrackedContentItem {
    progress: number;
    elapsed_time: number; // eslint-disable-line camelcase
    viewport_height: number; // eslint-disable-line camelcase
}

/**
 * Returns list of objects which returns position of
 * main element for each scroll threshold
 */
const getItemsToTrack = () => {
    const element = document.querySelector("main");
    const elementTop = element.offsetTop;
    const elementHeight = element.offsetHeight;
    const thresholds = [25, 50, 75, 100];

    const itemsToTrack: TrackableItem[] = [];
    thresholds.forEach((threshold) => {
        const elementPosition = ((elementHeight / 100) * threshold) + elementTop;
        itemsToTrack.push({
            top: elementPosition,
            bottom: elementPosition,
            progress: threshold
        });
    });
    return itemsToTrack;
};

const date = new Date();
const loadTime = date.getTime();

/**
 * useContentPercentViewed will return the scroll progress
 * for each scroll percentage threshold.
 */
export const useContentPercentViewed = () => {
    if (!isBrowser() || !document.querySelector("main")) {
        return null;
    }

    const [state, setState] = React.useState<TrackedContentItem[]>([]);
    const elementItemsToTrack = getItemsToTrack();

    const trackItems = (itemsToTrack: TrackableItem[]) => {
        const topOfScreen = window.pageYOffset;
        const bottomOfScreen = topOfScreen + window.innerHeight;
        itemsToTrack.forEach((item) => {
            const isTopInViewport = item.top > topOfScreen && item.top < bottomOfScreen;
            const isBottomInViewport = item.bottom > topOfScreen && item.bottom < bottomOfScreen;
            const isMiddleEltInViewport = item.top < topOfScreen && item.bottom > bottomOfScreen;
            const isProgressTracked = state.find((stateObj) => stateObj.progress === item.progress);
            if (!isProgressTracked && (isTopInViewport || isBottomInViewport || isMiddleEltInViewport)) {
                setState([
                    ...state,
                    {
                        progress: item.progress,
                        elapsed_time: (new Date().getTime() - loadTime) / 1000,
                        viewport_height: bottomOfScreen - topOfScreen
                    }
                ]);
            }
        });
    };

    React.useEffect(() => {
        const handleScroll = () => {
            trackItems(elementItemsToTrack);
        };

        handleScroll();
        window.addEventListener("scroll", handleScroll, { passive: true });
        return () => {
            window.removeEventListener("scroll", handleScroll);
        };
    }, [state]);

    return state;
};
